import { useQuery } from '@tanstack/react-query';
import { dataApi } from '../../../api/data';
import type { BatchQueryItem } from '../../../api/data';
import { SOURCES } from '../../../config';
import type { QueryResponse, BatchQueryResponse } from '../../../types';

const today = () => new Date().toISOString().split('T')[0];

// 5 dakika staleTime - sayfa içi navigasyonda tekrar sorgu atmaz
const STALE_TIME = 5 * 60 * 1000;

// Helper: batch response'tan belirli bir key'in verisini çek
function getQueryResult<T>(
  batchData: BatchQueryResponse | undefined,
  key: string,
): QueryResponse<T> | undefined {
  if (!batchData?.results?.[key]) return undefined;
  return batchData.results[key] as unknown as QueryResponse<T>;
}

// Kademe 1 - Bilanço verileri (tek batch request)
function useTier1BatchQuery() {
  const queries: BatchQueryItem[] = [
    { key: 'bankalar', sourceId: SOURCES.BANKALAR, parameters: { BaslangicTarihi: '2025-12-31', BitisTarihi: today() } },
    { key: 'kasa', sourceId: SOURCES.KASA_HAREKETLERI, parameters: { BitisTarihi: today() } },
    { key: 'borclular', sourceId: SOURCES.BORCLULAR, parameters: {} },
    { key: 'alacaklilar', sourceId: SOURCES.ALACAKLILAR, parameters: {} },
    { key: 'alinanCekler', sourceId: SOURCES.ALINAN_CEKLER, parameters: {} },
    { key: 'verilenCekler', sourceId: SOURCES.VERILEN_CEKLER, parameters: {} },
    { key: 'stokMaliyet', sourceId: SOURCES.STOK_MALIYET, parameters: { pRaporTarihi: today() } },
  ];

  return useQuery({
    queryKey: ['dashboard-tier1-batch'],
    queryFn: async () => {
      const response = await dataApi.executeBatchQuery(queries, 90);
      return response.data;
    },
    staleTime: STALE_TIME,
  });
}

// Kademe 2 - Analitik (grafikler, bilanço bittikten sonra - tek batch request)
function useTier2BatchQuery(
  tier1Ready: boolean,
  dateRange: { start: string; end: string },
  faaliyetDateRange: { start: string; end: string },
) {
  const queries: BatchQueryItem[] = [
    { key: 'fire', sourceId: SOURCES.FIRE_HAREKETLERI, parameters: {} },
    { key: 'giderler', sourceId: SOURCES.GIDERLER, parameters: {} },
    { key: 'topSatisMiktar', sourceId: SOURCES.TOP_SATIS_STOKLARI_SUBE, parameters: { BaslangicTarihi: dateRange.start, BitisTarihi: dateRange.end, TopKayit: 10 } },
    { key: 'topSatisTutar', sourceId: SOURCES.TOP_SATIS_STOKLARI, parameters: { BaslangicTarihi: dateRange.start, BitisTarihi: dateRange.end, TopKayit: 10 } },
    { key: 'onlineCiro', sourceId: SOURCES.ONLINE_CIRO_TAKIP, parameters: { BaslangicTarihi: dateRange.start, BitisTarihi: dateRange.end } },
    { key: 'faaliyet', sourceId: SOURCES.FAALIYET_RAPORU, parameters: { BaslangicTarihi: faaliyetDateRange.start, BitisTarihi: faaliyetDateRange.end } },
  ];

  return useQuery({
    queryKey: ['dashboard-tier2-batch', dateRange, faaliyetDateRange],
    queryFn: async () => {
      const response = await dataApi.executeBatchQuery(queries, 90);
      return response.data;
    },
    enabled: tier1Ready,
    staleTime: STALE_TIME,
  });
}

export interface TieredQueryResult {
  tier1Batch: ReturnType<typeof useTier1BatchQuery>;
  tier2Batch: ReturnType<typeof useTier2BatchQuery>;
  tier1Ready: boolean;
  getResult: <T>(tier: 'tier1' | 'tier2', key: string) => QueryResponse<T> | undefined;
}

export function useTieredQueries(
  dateRange: { start: string; end: string },
  faaliyetDateRange: { start: string; end: string },
): TieredQueryResult {
  const tier1Batch = useTier1BatchQuery();
  const tier1Ready = !tier1Batch.isLoading;
  const tier2Batch = useTier2BatchQuery(tier1Ready, dateRange, faaliyetDateRange);

  function getResult<T>(tier: 'tier1' | 'tier2', key: string): QueryResponse<T> | undefined {
    const batchData = tier === 'tier1' ? tier1Batch.data : tier2Batch.data;
    return getQueryResult<T>(batchData, key);
  }

  return { tier1Batch, tier2Batch, tier1Ready, getResult };
}
